/**
 * The "You're in!" moment — a confetti burst + welcome card shown when entitlement lands
 * (successful claim or checkout return). Self-contained canvas confetti, no dependencies.
 */
import { useEffect, useRef } from 'react'

const COLORS = ['#7c5bff', '#ff8ab0', '#2fa37a', '#ffd166', '#56b8ff', '#ffffff']

export function Celebration({ onDone }: { onDone: () => void }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return
    const w = (canvas.width = window.innerWidth)
    const h = (canvas.height = window.innerHeight)
    interface P { x: number; y: number; vx: number; vy: number; r: number; rot: number; vr: number; c: string }
    const parts: P[] = []
    for (let i = 0; i < 160; i += 1) {
      parts.push({
        x: w / 2 + (Math.random() - 0.5) * w * 0.3,
        y: h * 0.42,
        vx: (Math.random() - 0.5) * 520,
        vy: -260 - Math.random() * 420,
        r: 4 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 12,
        c: COLORS[i % COLORS.length]
      })
    }
    let raf = 0
    let last = performance.now()
    let elapsed = 0
    const loop = (): void => {
      const now = performance.now()
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      elapsed += dt
      ctx.clearRect(0, 0, w, h)
      for (const p of parts) {
        p.vy += 620 * dt // gravity
        p.vx *= 1 - 0.6 * dt // drag
        p.x += p.vx * dt
        p.y += p.vy * dt
        p.rot += p.vr * dt
        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rot)
        ctx.globalAlpha = Math.max(0, Math.min(1, 4.2 - elapsed))
        ctx.fillStyle = p.c
        ctx.fillRect(-p.r / 2, -p.r / 4, p.r, p.r / 2)
        ctx.restore()
      }
      if (elapsed < 4.5) raf = requestAnimationFrame(loop)
      else onDone()
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [onDone])

  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 40, pointerEvents: 'none' }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0 }} />
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
        <div
          onClick={onDone}
          style={{
            pointerEvents: 'auto', cursor: 'pointer', textAlign: 'center', color: '#fff',
            background: 'rgba(10,10,18,0.85)', border: '1px solid rgba(124,91,255,0.6)',
            borderRadius: 16, padding: '28px 44px', fontFamily: 'monospace',
            boxShadow: '0 0 60px rgba(124,91,255,0.35)'
          }}
        >
          <div style={{ fontFamily: "'Changa', sans-serif", fontWeight: 700, fontSize: 34, letterSpacing: 3 }}>
            You&apos;re in!
          </div>
          <div style={{ fontSize: 13, opacity: 0.75, marginTop: 6 }}>
            Welcome to SYQEL<span style={{ fontSize: 9, verticalAlign: 'super' }}>®</span> Art — enjoy the journey
          </div>
        </div>
      </div>
    </div>
  )
}
